import React, { useState } from 'react';
import { useOffice } from '../../../../../contexts/OfficeContext';
import { PrimaryButton, Spinner, SpinnerSize, MessageBar, MessageBarType } from '@fluentui/react';
import { theme } from '../../../../../styles';
import { useQuickAction } from '../../../../../contexts/QuickActionContext';
import { llmService } from '../../../../../services/api';
import { getEmailAttachmentsForBackend } from '../../../../../utils/helpers/attachmentBackend.helpers';

const SavePoint: React.FC = () => {
  const { currentEmail } = useOffice();
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const quickAction = useQuickAction();

  const buildExtractionPrompt = () => {
    return `Tu es un assistant spécialisé dans l'extraction d'informations depuis des emails pour créer des notes structurées.

INSTRUCTIONS IMPORTANTES :
- Extrait UNIQUEMENT les informations pertinentes de l'email
- La date doit être au format YYYY-MM-DD (utilise la date de l'email si non spécifiée dans le contenu)
- Le nom du parc éolien (windfarm) doit être extrait du sujet ou du corps de l'email
- Le topic doit être un résumé court et précis (max 100 caractères)
- Le comment doit contenir les détails importants de l'email sous format bullet point, sois concis et exhaustif
- Le type doit être l'une de ces catégories : "O&M", "operational", "invoice", "contract", "meeting", "incident", "maintenance", "other"
- Le company doit être le nom de l'entreprise mentionnée (si applicable)

TYPES DE NOTES :
- O&M : Opération et Maintenance
- operational : Opérationnel (mise en service, arrêts, production, etc.)
- invoice : Facturation (factures, paiements, devis)
- contract : Contractuel (contrats, avenants, négociations)
- meeting : Réunion (comptes-rendus, planification)
- incident : Incident (pannes, accidents, problèmes)
- maintenance : Maintenance préventive ou corrective
- other : Autre

EXEMPLES DE DÉTECTION :
- Email sur une facture → type: "invoice"
- Email sur un arrêt de machine → type: "operational"
- Email sur un compte-rendu de visite → type: "meeting"
- Email sur une panne → type: "incident"
- Email sur planification maintenance → type: "maintenance"

Tu dois retourner UNIQUEMENT un objet JSON valide avec cette structure :
{
  "date": "YYYY-MM-DD",
  "windfarm": "Nom du parc éolien",
  "topic": "Sujet court",
  "comment": "Détails complets extraits de l'email avec un format bullet point",
  "type": "O&M|operational|invoice|contract|meeting|incident|maintenance|other",
  "company": "Nom de l'entreprise (optionnel)"
}

IMPORTANT: 
- Return ONLY valid JSON, no markdown, no code blocks, no explanations
- Si tu ne trouves pas le nom du parc éolien, utilise "Unknown" pour windfarm
- Si tu ne trouves pas la date, utilise la date du jour au format YYYY-MM-DD
- Le type DOIT être l'une des valeurs listées ci-dessus
- Apres l'utilisation du tools, tu renverras un resume de ce que tu as extrait comme information a lutilisateur`;
  };

  const handleSavePoint = async () => {
    if (!currentEmail) {
      setStatus({ type: 'error', message: 'Aucun email sélectionné' });
      return;
    }

    setIsSaving(true);
    setStatus({ type: 'info', message: 'Extraction des informations et sauvegarde en cours...' });
    
    // Start QuickAction with LLM and MCP
    quickAction.startAction('savePoint', true, true);

    try {
      // Get attachments if available
      let attachments: any[] = [];
      try {
        attachments = await getEmailAttachmentsForBackend();
        console.log(`📎 [SavePoint] Fetched ${attachments.length} attachment(s)`);
      } catch (attError) {
        console.warn('⚠️ [SavePoint] Failed to fetch attachments:', attError);
      }

      // Build the email context for the LLM
      const emailContext = `
EMAIL SUBJECT: ${currentEmail.subject}
FROM: ${currentEmail.from}
DATE: ${new Date().toISOString().split('T')[0]}

EMAIL BODY:
${currentEmail.body}

${currentEmail.fullConversation ? `\nFULL CONVERSATION:\n${currentEmail.fullConversation}` : ''}
      `.trim();

      // Update status to using MCP
      quickAction.updateStatus('using_mcp', 'Utilisation de l\'outil MCP save_note...');
      
      // Update status to streaming
      quickAction.updateStatus('streaming', 'Sauvegarde de la note...');

      let fullResponse = '';

      // Use llmService for streaming with attachments
      for await (const chunk of llmService.streamPrompt({
        model: 'gpt-4o-mini',
        temperature: 0.2,
        maxTokens: 1000,
        useMcpTools: true,
        attachments: attachments.length > 0 ? attachments : undefined,
        messages: [
          {
            role: 'system',
            content: buildExtractionPrompt()
          },
          {
            role: 'user',
            content: `Extrait les informations de cet email et sauvegarde la note en utilisant l'outil save_note:\n\n${emailContext}`
          }
        ]
      })) {
        if (chunk.type === 'error') {
          throw new Error(chunk.error || 'Stream error');
        }
        
        if (chunk.type === 'chunk' && chunk.delta) {
          fullResponse += chunk.delta;
          quickAction.updateStreamedContent(fullResponse);
        }
        
        if (chunk.type === 'done') {
          fullResponse = chunk.fullText || fullResponse;
        }
      }

      console.log('Save Point Response:', fullResponse);
      quickAction.completeAction();
      setStatus({ 
        type: 'success', 
        message: 'Point sauvegardé avec succès! Vérifiez notes_database.json' 
      });

    } catch (error) {
      console.error('Error saving point:', error);
      const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
      quickAction.setError(errorMsg);
      setStatus({ 
        type: 'error', 
        message: `Erreur lors de la sauvegarde: ${errorMsg}` 
      });
    } finally {
      setIsSaving(false);
      // Reset after a delay
      setTimeout(() => quickAction.resetAction(), 3000);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
      <PrimaryButton
        text={isSaving ? 'Sauvegarde en cours...' : 'Sauvegarder Point'}
        onClick={handleSavePoint}
        disabled={isSaving || !currentEmail}
        styles={{
          root: {
            width: 220,
            height: 50,
            fontWeight: 600,
            borderRadius: theme.effects.roundedCorner2,
            boxShadow: theme.effects.elevation8,
          },
        }}
      />
      
      {isSaving && (
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <Spinner size={SpinnerSize.small} />
          <span style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
            Extraction et sauvegarde...
          </span>
        </div>
      )}

      {status && (
        <MessageBar
          messageBarType={
            status.type === 'success' ? MessageBarType.success :
            status.type === 'error' ? MessageBarType.error :
            MessageBarType.info
          }
          onDismiss={() => setStatus(null)}
          dismissButtonAriaLabel="Fermer"
        >
          {status.message}
        </MessageBar>
      )}
    </div>
  );
};

export default SavePoint;
